import './setGlobalVars';

import Cookies from 'js-cookie';
import { Container, createRoot } from 'react-dom/client';
import smoothscroll from 'smoothscroll-polyfill';
import { StyleSheetManager } from 'styled-components';

import { Language } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$types';
import {
	FormatHelper,
	LoggerHelper,
	TranslationHelper,
} from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { LoggerConfig } from '@configs';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { defaultI18n } from '@i18n';
import {
	BaseSessionInfo,
	BaseSessionInfoFlow,
	BaseSessionInfoLanguage,
	BaseSessionInfoLoanType,
	SessionInfoJourneyData,
} from '@types';

import { StartupApp } from './startupApp';

smoothscroll.polyfill();

const startApp = (sessionInfo: BaseSessionInfo, targetElement = document.getElementById('root')): void => {
	if (sessionInfo) {
		LoggerHelper.init(LoggerConfig, { all: true, flow: true, core: true, sessionInfo: true });
		TranslationHelper.register(
			'global',
			{
				default: {
					ab:
						FormatHelper.formatLanguageCode(sessionInfo.language) === Language.pt
							? defaultI18n.pt
							: defaultI18n.en,
					bcp:
						FormatHelper.formatLanguageCode(sessionInfo.language) === Language.pt
							? defaultI18n.pt
							: defaultI18n.en,
				},
				pt: {
					ab:
						FormatHelper.formatLanguageCode(sessionInfo.language) === Language.pt
							? defaultI18n.pt
							: defaultI18n.en,
					bcp:
						FormatHelper.formatLanguageCode(sessionInfo.language) === Language.pt
							? defaultI18n.pt
							: defaultI18n.en,
				},
				en: { ab: defaultI18n.en, bcp: defaultI18n.en },
			},
			{
				addAndReplace: true,
				siteName: 'global',
				listName: 'global',
			}
		);

		const root = createRoot(targetElement as Container);

		if (sessionInfo.shadow) {
			const cache = createCache({ key: 'react', container: sessionInfo.shadow });

			root.render(
				<StyleSheetManager target={sessionInfo.shadow}>
					<CacheProvider value={cache}>
						<StartupApp sessionInfo={sessionInfo} />
					</CacheProvider>
				</StyleSheetManager>
			);
		} else {
			root.render(<StartupApp sessionInfo={sessionInfo} />);
		}
	} else {
		// eslint-disable-next-line no-console
		console.error('Session Info not found!');
	}
};

interface ContextValues extends BaseSessionInfo {
	journeyData: Omit<SessionInfoJourneyData, 'flow' & 'loanType'>;
}

interface ReactWidgetsApiInput {
	targetHtmlElement: HTMLElement;
	contextValues: ContextValues;
}

export const api = {
	loadPublicCLSimulator: ({ contextValues, targetHtmlElement }: ReactWidgetsApiInput) =>
		startApp(
			{
				...contextValues,
				language:
					FormatHelper.formatLanguageCode(contextValues.language) === Language.pt
						? BaseSessionInfoLanguage.PT
						: BaseSessionInfoLanguage.EN,
				journeyData: {
					...contextValues.journeyData,
					flow: BaseSessionInfoFlow.clPublicSimulator,
					loanType: BaseSessionInfoLoanType.CL,
				},
			},
			targetHtmlElement
		),
	loadPublicALSimulator: ({ contextValues, targetHtmlElement }: ReactWidgetsApiInput) =>
		startApp(
			{
				...contextValues,
				language:
					FormatHelper.formatLanguageCode(contextValues.language) === Language.pt
						? BaseSessionInfoLanguage.PT
						: BaseSessionInfoLanguage.EN,
				journeyData: {
					...contextValues.journeyData,
					flow: BaseSessionInfoFlow.clPublicSimulator,
					loanType: BaseSessionInfoLoanType.AL,
				},
			},
			targetHtmlElement
		),
	loadPublicRENENSimulator: ({ contextValues, targetHtmlElement }: ReactWidgetsApiInput) =>
		startApp(
			{
				...contextValues,
				language:
					FormatHelper.formatLanguageCode(contextValues.language) === Language.pt
						? BaseSessionInfoLanguage.PT
						: BaseSessionInfoLanguage.EN,
				journeyData: {
					...contextValues.journeyData,
					flow: BaseSessionInfoFlow.clPublicSimulator,
					loanType: BaseSessionInfoLoanType.RENEN,
				},
			},
			targetHtmlElement
		),
	loadPublicALEVSimulator: ({ contextValues, targetHtmlElement }: ReactWidgetsApiInput) =>
		startApp(
			{
				...contextValues,
				language:
					FormatHelper.formatLanguageCode(contextValues.language) === Language.pt
						? BaseSessionInfoLanguage.PT
						: BaseSessionInfoLanguage.EN,
				journeyData: {
					...contextValues.journeyData,
					flow: BaseSessionInfoFlow.clPublicSimulator,
					loanType: BaseSessionInfoLoanType.ALEV,
				},
			},
			targetHtmlElement
		),
	loadPublicHOMESimulator: ({ contextValues, targetHtmlElement }: ReactWidgetsApiInput) =>
		startApp(
			{
				...contextValues,
				language:
					FormatHelper.formatLanguageCode(contextValues.language) === Language.pt
						? BaseSessionInfoLanguage.PT
						: BaseSessionInfoLanguage.EN,
				journeyData: {
					...contextValues.journeyData,
					flow: BaseSessionInfoFlow.clPublicSimulator,
					loanType: BaseSessionInfoLoanType.HOME,
				},
			},
			targetHtmlElement
		),
	loadPublicEDUCASimulator: ({ contextValues, targetHtmlElement }: ReactWidgetsApiInput) =>
		startApp(
			{
				...contextValues,
				language:
					FormatHelper.formatLanguageCode(contextValues.language) === Language.pt
						? BaseSessionInfoLanguage.PT
						: BaseSessionInfoLanguage.EN,
				journeyData: {
					...contextValues.journeyData,
					flow: BaseSessionInfoFlow.clPublicSimulator,
					loanType: BaseSessionInfoLoanType.EDUCA,
				},
			},
			targetHtmlElement
		),
	loadPublicHEALTHSimulator: ({ contextValues, targetHtmlElement }: ReactWidgetsApiInput) =>
		startApp(
			{
				...contextValues,
				language:
					FormatHelper.formatLanguageCode(contextValues.language) === Language.pt
						? BaseSessionInfoLanguage.PT
						: BaseSessionInfoLanguage.EN,
				journeyData: {
					...contextValues.journeyData,
					flow: BaseSessionInfoFlow.clPublicSimulator,
					loanType: BaseSessionInfoLoanType.HEALTH,
				},
			},
			targetHtmlElement
		),
	loadPublicCONSTRSimulator: ({ contextValues, targetHtmlElement }: ReactWidgetsApiInput) =>
		startApp(
			{
				...contextValues,
				language:
					FormatHelper.formatLanguageCode(contextValues.language) === Language.pt
						? BaseSessionInfoLanguage.PT
						: BaseSessionInfoLanguage.EN,
				journeyData: {
					...contextValues.journeyData,
					flow: BaseSessionInfoFlow.clPublicSimulator,
					loanType: BaseSessionInfoLoanType.CONSTR,
				},
			},
			targetHtmlElement
		),
	loadPublicANSimulator: ({ contextValues, targetHtmlElement }: ReactWidgetsApiInput) =>
		startApp(
			{
				...contextValues,
				language:
					FormatHelper.formatLanguageCode(contextValues.language) === Language.pt
						? BaseSessionInfoLanguage.PT
						: BaseSessionInfoLanguage.EN,
				journeyData: {
					...contextValues.journeyData,
					flow: BaseSessionInfoFlow.clPublicSimulator,
					loanType: BaseSessionInfoLoanType.AN,
				},
			},
			targetHtmlElement
		),
};

const sessionInfoCookie = Cookies.get('SessionInfo');

if (sessionInfoCookie && process.env.NODE_ENV === 'development') {
	const sessionInfo = { ...JSON.parse(sessionInfoCookie) };
	const targetElement = document.getElementById('root');

	if (targetElement) {
		let loanType = BaseSessionInfoLoanType.CL;

		if (sessionInfo.flow === 'ALSimulator') {
			loanType = BaseSessionInfoLoanType.AL;
		} else if (sessionInfo.flow === 'RENENSimulator') {
			loanType = BaseSessionInfoLoanType.RENEN;
		} else if (sessionInfo.flow === 'ALEVSimulator') {
			loanType = BaseSessionInfoLoanType.ALEV;
		} else {
			loanType = BaseSessionInfoLoanType.CL;
		}

		startApp(
			{
				...sessionInfo,
				api: {
					...sessionInfo.api,
					subscriptionKey: sessionInfo.ocpApimSubscriptionKey,
					tokenOAuth: sessionInfo.tokenOAuth,
				},
				journeyData: {
					...sessionInfo.journeyData,
					flow: BaseSessionInfoFlow.clPublicSimulator,
					loanType,
				},
			},
			targetElement
		);
	}
}
